<template>
  <div
    class="tw-bg-branding-blue-50 tw-border-2 tw-border-branding-blue-400 tw-p-2 tw-rounded-sm tw-mb-4 tw-mx-4"
    :data-test-id="dataTestId"
  >
    <div
      class="tw-flex tw-justify-between tw-items-center xl:tw-flex-wrap 2xl:tw-flex-nowrap"
    >
      <div class="tw-flex tw-gap-2">
        <div>
          <div
            v-tippy="{
              content: tooltipText,
              placement: 'top',
            }"
          >
            <div
              class="tw-w-6 tw-h-6 tw-p-0.5 tw-bg-white tw-rounded-full tw-flex tw-items-center tw-justify-center tw-place-self-center"
            >
              <component :is="icon" width="16" height="16" />
            </div>
          </div>
        </div>
        <div>
          <div class="tw-font-semibold">
            <slot name="title" />
          </div>
          <div class="tw-text-sm">
            <slot name="text" />
          </div>
        </div>
      </div>
      <div class="xl:tw-ml-auto 2xl:tw-ml-0">
        <a
          :href="docsLink"
          target="_blank"
          :data-test-id="`${dataTestId}-learn-more-external-link`"
        >
          <cl-button
            variant="secondary"
            size="small"
            class="tw-text-nowrap tw-self-center"
            :data-test-id="`${dataTestId}-learn-more-button`"
          >
            <div class="tw-flex tw-gap-1 tw-items-center">
              {{ $t("learn-more") }}
              <cb-icon-external-link :width="16" />
            </div>
          </cl-button>
        </a>
      </div>
    </div>
  </div>
</template>

<script setup lang="ts">
import { EXTERNAL_DOCS_LINKS } from "@/constants/externalDocsLinks";
import { SERVICE_ICON } from "@/utils/services";
import { computed } from "vue";
import { useI18n } from "vue-i18n";
import { useFlag } from "@/composables/useFlags/useFlag";

const { t } = useI18n();
type Solution = "EMAIL_SECURITY" | "PHISHING_SIMULATION" | "SAT";

type Props = {
  type: Solution;
  dataTestId: string;
};

const props = defineProps<Props>();
const { isFeatureAvailable } = useFlag("DASHBOARD_EMAIL_ARCHIVING_CARD");

const icon = computed(
  () =>
    ({
      EMAIL_SECURITY: SERVICE_ICON.PHISH_SERVICE,
      PHISHING_SIMULATION: SERVICE_ICON.PHISHING_SIMULATION,
      SAT: SERVICE_ICON.SAT_SERVICE,
    })[props.type],
);

const docsLink = computed(
  () =>
    ({
      EMAIL_SECURITY: isFeatureAvailable.value
        ? EXTERNAL_DOCS_LINKS.EMAIL_SECURITY_MARKETING_PAGE
        : EXTERNAL_DOCS_LINKS.EMAIL_SECURITY,
      PHISHING_SIMULATION: EXTERNAL_DOCS_LINKS.TITANHQ_CONTACT_PAGE,
      SAT: EXTERNAL_DOCS_LINKS.TITANHQ_CONTACT_PAGE,
    })[props.type],
);

const tooltipText = computed(
  () =>
    ({
      EMAIL_SECURITY: t("email-security-solution"),
      PHISHING_SIMULATION: t("phishing-simulation-label"),
      SAT: t("sat-label"),
    })[props.type],
);
</script>
